import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MODULOS, MODULOS_ACTIVOS } from '../config/modulos';
import { usePerfil } from '../context/PerfilContext';
import { ChevronRight, Lock, Sparkles } from 'lucide-react';
import { nombreDeUsuario } from '../utils/usuario';
import { AvisosAMedias, useNumeroAvisosAMedias } from '../components/amedias/AvisosAMedias';
import { NOMBRE_APP } from '../config/marca';
import { HoyTareas } from '../components/tareas/HoyTareas';
import { AvisosPrestamos, useNumeroPrestamosVencidos } from '../components/prestamos/AvisosPrestamos';

export const Inicio: React.FC = () => {
  const { user } = useAuth();
  const { tieneModulo, esAdmin, puedeEscribirPrestamos } = usePerfil();
  const avisosAMedias = useNumeroAvisosAMedias();
  const prestamosVencidos = useNumeroPrestamosVencidos();

  // Un usuario normal solo ve lo que tiene activado. Quien administra ve
  // además lo que falta por construir, para no perder de vista el plan.
  const visibles = esAdmin
    ? MODULOS
    : MODULOS_ACTIVOS.filter(m => tieneModulo(m.id));

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{NOMBRE_APP}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Hola, {nombreDeUsuario(user)} 👋 Elige con qué quieres trabajar.
        </p>
      </header>

      {/* Lo que requiere atención, nada más entrar */}
      <AvisosAMedias />
      <AvisosPrestamos />
      <HoyTareas />

      <div className="grid gap-4 sm:grid-cols-2">
        {visibles.map(m => {
          const contenido = (
            <>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${m.acento.fondoSuave}`}>
                <m.Icon className={`w-6 h-6 ${m.acento.texto}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{m.nombre}</h2>
                  {m.id === 'amedias' && avisosAMedias > 0 && (
                    <span title={`${avisosAMedias} aviso(s)`}
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold
                                 flex items-center justify-center">
                      {avisosAMedias}
                    </span>
                  )}
                  {m.id === 'prestamos' && prestamosVencidos > 0 && (
                    <span title={`${prestamosVencidos} pasado(s) de fecha`}
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold
                                 flex items-center justify-center">
                      {prestamosVencidos}
                    </span>
                  )}
                  {m.id === 'prestamos' && !puedeEscribirPrestamos && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide
                                     text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3" /> Plus
                    </span>
                  )}
                  {!m.disponible && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide
                                     text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                      <Lock className="w-3 h-3" /> Pronto
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{m.descripcion}</p>
              </div>
              {m.disponible && <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />}
            </>
          );

          const clasesBase =
            'flex items-start gap-4 p-5 rounded-xl border bg-white dark:bg-gray-800 transition';

          if (!m.disponible) {
            return (
              <div key={m.id}
                className={`${clasesBase} border-dashed border-gray-300 dark:border-gray-700 opacity-60 cursor-not-allowed`}>
                {contenido}
              </div>
            );
          }

          return (
            <Link key={m.id} to={m.inicio}
              className={`${clasesBase} border-gray-200 dark:border-gray-700 shadow-sm
                          hover:shadow-md hover:-translate-y-0.5 active:translate-y-0`}>
              {contenido}
            </Link>
          );
        })}
      </div>
    </div>
  );
};
